/**
 * Fix Problems — the owner's guided data-correction hub (settings section).
 *
 * Three task-shaped fixes, each a small wizard:
 *   pick entities → preview (read-only plan, plain language) → type the
 *   name being merged/moved away + PIN → apply with the reviewed planHash.
 *
 * Below the tasks: the corrections history with per-row Undo. Undo is
 * blocked (greyed) when later changes were saved after a fix; the backend
 * additionally fingerprint-verifies every undo, so it either restores the
 * books exactly or changes nothing.
 */
import { useMemo, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  AlertTriangle,
  ArrowLeftRight,
  CheckCircle2,
  GitMerge,
  History,
  Undo2,
  Users2,
} from 'lucide-react';
import {
  ApiError,
  api,
  corrections,
  type Account,
  type CorrectionHistoryRow,
  type CorrectionOpType,
  type CorrectionParams,
  type CorrectionPlan,
  type Party,
} from '../api';
import { AppShell } from '../ui/AppShell';
import { PageHeader } from '../ui/PageHeader';
import { SectionCard, Money } from '../ui/components';
import {
  Button,
  Checkbox,
  EmptyState,
  ErrorBanner,
  Pill,
  Select,
  Spinner,
  TextInput,
} from '../ui/primitives';
import { usePinGate } from '../auth/pinGate';

// ---------------------------------------------------------------------------
// Plain-language dictionaries
// ---------------------------------------------------------------------------

const TABLE_LABELS: Record<string, [singular: string, plural: string]> = {
  ledger_entries: ['money line', 'money lines'],
  settlements: ['weekly settlement', 'weekly settlements'],
  assignments: ['account-holder record', 'account-holder records'],
  opening_balances: ['opening balance', 'opening balances'],
  payments: ['payment', 'payments'],
  adjustments: ['adjustment', 'adjustments'],
  client_aliases: ['sheet nickname', 'sheet nicknames'],
  account_aliases: ['account nickname', 'account nicknames'],
  raw_upline_rows: ['imported sheet row', 'imported sheet rows'],
  match_accounts: ['linked match book', 'linked match books'],
  cash_accounts: ['linked cash book', 'linked cash books'],
};

function rowsLabel(table: string, count: number): string {
  const pair = TABLE_LABELS[table] ?? [table, table];
  return `${count} ${count === 1 ? pair[0] : pair[1]}`;
}

interface TaskDef {
  op: CorrectionOpType;
  icon: typeof GitMerge;
  title: string;
  description: string;
}

const TASKS: TaskDef[] = [
  {
    op: 'merge-account',
    icon: GitMerge,
    title: 'Two accounts are actually the same',
    description:
      'A duplicate account was created for the same real account. Fold everything under one name — nothing about the money changes.',
  },
  {
    op: 'merge-party',
    icon: Users2,
    title: 'Two people are actually the same',
    description:
      'The same person exists twice (a duplicate or a misspelling). Move all their history under one name.',
  },
  {
    op: 'move-holder-history',
    icon: ArrowLeftRight,
    title: 'History is under the wrong person',
    description:
      "An account's settled weeks were booked to the wrong person. Move that history to the right person.",
  },
];

// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------

export function FixProblemsPage() {
  const [activeOp, setActiveOp] = useState<CorrectionOpType | null>(null);

  const partiesQ = useQuery<Party[]>({
    queryKey: ['parties', 'all'],
    queryFn: () => api.get('/api/parties?status=all'),
  });
  const accountsQ = useQuery<Account[]>({
    queryKey: ['accounts', 'fix-problems'],
    queryFn: () => api.get('/api/accounts'),
  });

  const parties = useMemo(() => partiesQ.data ?? [], [partiesQ.data]);
  const accounts = useMemo(() => accountsQ.data ?? [], [accountsQ.data]);
  const partyName = (id: number): string =>
    parties.find((p) => p.id === id)?.displayName ?? `person #${id}`;
  const accountName = (id: number): string =>
    accounts.find((a) => a.id === id)?.canonicalName ?? `account #${id}`;

  const toolbar = (
    <PageHeader
      section="settings"
      title="Fix problems"
      subtitle="Guided fixes for identity mistakes — duplicates and wrong-person history. Every fix shows exactly what will change before anything happens, saves a safety copy first, and can be undone."
      breadcrumb={[{ label: 'Settings', to: '/settings' }, { label: 'Fix problems' }]}
    />
  );

  return (
    <AppShell title="Fix problems" toolbar={toolbar}>
      <div className="mx-auto max-w-5xl space-y-4">
        <div className="grid gap-4 md:grid-cols-3">
          {TASKS.map((t) => (
            <TaskCard
              key={t.op}
              task={t}
              active={activeOp === t.op}
              onStart={() => setActiveOp(activeOp === t.op ? null : t.op)}
            />
          ))}
        </div>

        {activeOp && (
          <CorrectionWizard
            key={activeOp}
            op={activeOp}
            parties={parties}
            accounts={accounts}
            partyName={partyName}
            accountName={accountName}
            onClose={() => setActiveOp(null)}
          />
        )}

        <HistoryCard partyName={partyName} accountName={accountName} />
      </div>
    </AppShell>
  );
}

function TaskCard({
  task,
  active,
  onStart,
}: {
  task: TaskDef;
  active: boolean;
  onStart: () => void;
}) {
  const Icon = task.icon;
  return (
    <button
      type="button"
      onClick={onStart}
      aria-expanded={active}
      className="app-shell-surface group flex h-full flex-col items-start gap-3 rounded-[26px] border px-4 py-4 text-left shadow-sm backdrop-blur transition-all duration-150 hover:-translate-y-px hover:bg-surface-raised"
      style={active ? { borderColor: 'var(--sec-settings)' } : undefined}
    >
      <span className="flex h-11 w-11 items-center justify-center rounded-[18px] bg-[var(--sec-settings-tint)] text-[var(--sec-settings)]">
        <Icon className="h-[18px] w-[18px]" strokeWidth={2} />
      </span>
      <span className="text-small font-semibold tracking-[-0.01em] text-ink">{task.title}</span>
      <span className="text-[12px] leading-5 text-ink-muted">{task.description}</span>
      <span className="mt-auto text-caption font-semibold text-[var(--sec-settings)] group-hover:underline">
        {active ? 'Close ×' : 'Start →'}
      </span>
    </button>
  );
}

// ---------------------------------------------------------------------------
// Wizard
// ---------------------------------------------------------------------------

interface WizardProps {
  op: CorrectionOpType;
  parties: Party[];
  accounts: Account[];
  partyName: (id: number) => string;
  accountName: (id: number) => string;
  onClose: () => void;
}

function CorrectionWizard({ op, parties, accounts, partyName, accountName, onClose }: WizardProps) {
  const qc = useQueryClient();
  const { withPin } = usePinGate();
  const task = TASKS.find((t) => t.op === op)!;

  // --- selections ---
  const [sel, setSel] = useState<Record<string, string>>({});
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [archiveFrom, setArchiveFrom] = useState(false);

  // --- preview + confirm state ---
  const [plan, setPlan] = useState<CorrectionPlan | null>(null);
  const [previewing, setPreviewing] = useState(false);
  const [previewErr, setPreviewErr] = useState<string | null>(null);
  const [typed, setTyped] = useState('');
  const [applying, setApplying] = useState(false);
  const [applyErr, setApplyErr] = useState<string | null>(null);
  const [done, setDone] = useState<{ journalId: number } | null>(null);

  const pick = (key: string) => (value: string) => {
    setSel((s) => ({ ...s, [key]: value }));
    setPlan(null);
    setPreviewErr(null);
    setTyped('');
    setApplyErr(null);
  };

  const partyOptions = (opts?: { clientsOnly?: boolean; excludeSelf?: boolean }) =>
    parties
      .filter((p) => (opts?.clientsOnly ? p.role === 'client' : true))
      .filter((p) => (opts?.excludeSelf ? p.role !== 'self' : true))
      .map((p) => ({
        value: String(p.id),
        label: `${p.displayName}${p.status === 'archived' ? ' (archived)' : ''}${
          p.role !== 'client' ? ` — ${p.role}` : ''
        }`,
      }));

  const accountOptions = accounts.map((a) => ({
    value: String(a.id),
    label: `${a.canonicalName}${a.status !== 'active' ? ` (${a.status})` : ''}`,
  }));

  const params: CorrectionParams | null = useMemo(() => {
    const n = (key: string): number | null => {
      const v = parseInt(sel[key] ?? '', 10);
      return Number.isFinite(v) ? v : null;
    };
    switch (op) {
      case 'merge-account': {
        const loserAccountId = n('loser');
        const survivorAccountId = n('survivor');
        if (loserAccountId === null || survivorAccountId === null) return null;
        return { loserAccountId, survivorAccountId };
      }
      case 'merge-party': {
        const loserPartyId = n('loser');
        const survivorPartyId = n('survivor');
        if (loserPartyId === null || survivorPartyId === null) return null;
        return { loserPartyId, survivorPartyId };
      }
      case 'move-holder-history': {
        const accountId = n('account');
        const fromPartyId = n('from');
        const toPartyId = n('to');
        if (accountId === null || fromPartyId === null || toPartyId === null) return null;
        return {
          accountId,
          fromPartyId,
          toPartyId,
          ...(fromDate ? { fromDate } : {}),
          ...(toDate ? { toDate } : {}),
          ...(archiveFrom ? { archiveFromParty: true } : {}),
        };
      }
    }
  }, [op, sel, fromDate, toDate, archiveFrom]);

  /** The name the owner must type to confirm — the entity being folded/moved away. */
  const doomedName: string | null = useMemo(() => {
    if (!params) return null;
    if (op === 'merge-account') return accountName((params as { loserAccountId: number }).loserAccountId);
    if (op === 'merge-party') return partyName((params as { loserPartyId: number }).loserPartyId);
    return partyName((params as { fromPartyId: number }).fromPartyId);
  }, [op, params, accountName, partyName]);

  const runPreview = async (): Promise<void> => {
    if (!params) return;
    setPreviewing(true);
    setPreviewErr(null);
    setPlan(null);
    setTyped('');
    try {
      setPlan(await corrections.preview(op, params));
    } catch (err) {
      setPreviewErr(err instanceof Error ? err.message : 'Could not check this fix.');
    } finally {
      setPreviewing(false);
    }
  };

  const canConfirm =
    plan !== null &&
    plan.blockers.length === 0 &&
    plan.globalNetUnchanged &&
    doomedName !== null &&
    typed.trim().toLowerCase() === doomedName.trim().toLowerCase() &&
    !applying &&
    done === null;

  const runApply = async (): Promise<void> => {
    if (!plan || !params) return;
    setApplying(true);
    setApplyErr(null);
    try {
      await withPin(async () => {
        try {
          const res = await corrections.apply(op, params, plan.planHash);
          setDone({ journalId: res.journalId });
          void qc.invalidateQueries({ queryKey: ['corrections'] });
          void qc.invalidateQueries({ queryKey: ['parties'] });
          void qc.invalidateQueries({ queryKey: ['accounts'] });
        } catch (err) {
          if (err instanceof ApiError && err.status === 423) throw err; // PIN modal takes over
          setApplyErr(err instanceof Error ? err.message : 'The fix could not be applied.');
        }
      });
    } finally {
      setApplying(false);
    }
  };

  return (
    <SectionCard
      section="settings"
      title={task.title}
      action={
        <Button type="button" variant="ghost" size="sm" onClick={onClose}>
          Close
        </Button>
      }
    >
      <div className="space-y-4">
        {/* Step 1 — pick entities */}
        {op === 'merge-account' && (
          <div className="grid gap-3 sm:grid-cols-2">
            <Select
              label="Account to KEEP"
              value={sel.survivor ?? ''}
              onChange={pick('survivor')}
              options={accountOptions}
              placeholder="Choose the account that stays…"
            />
            <Select
              label="Duplicate account to fold in"
              value={sel.loser ?? ''}
              onChange={pick('loser')}
              options={accountOptions.filter((o) => o.value !== (sel.survivor ?? ''))}
              placeholder="Choose the duplicate…"
              hint="Its history moves to the kept account, then the duplicate is removed. Its name is kept as a nickname so future sheets still match."
            />
          </div>
        )}
        {op === 'merge-party' && (
          <div className="grid gap-3 sm:grid-cols-2">
            <Select
              label="Person to KEEP"
              value={sel.survivor ?? ''}
              onChange={pick('survivor')}
              options={partyOptions()}
              placeholder="Choose who stays…"
            />
            <Select
              label="Duplicate person to fold in"
              value={sel.loser ?? ''}
              onChange={pick('loser')}
              options={partyOptions({ excludeSelf: true }).filter(
                (o) => o.value !== (sel.survivor ?? ''),
              )}
              placeholder="Choose the duplicate…"
              hint="All their money history moves to the kept person; the duplicate is archived (reversible)."
            />
          </div>
        )}
        {op === 'move-holder-history' && (
          <div className="space-y-3">
            <div className="grid gap-3 sm:grid-cols-3">
              <Select
                label="Account"
                value={sel.account ?? ''}
                onChange={pick('account')}
                options={accountOptions}
                placeholder="Choose the account…"
              />
              <Select
                label="Currently under (wrong person)"
                value={sel.from ?? ''}
                onChange={pick('from')}
                options={partyOptions({ excludeSelf: true })}
                placeholder="Choose the wrong person…"
              />
              <Select
                label="Should be under"
                value={sel.to ?? ''}
                onChange={pick('to')}
                options={partyOptions({ clientsOnly: true }).filter(
                  (o) => o.value !== (sel.from ?? ''),
                )}
                placeholder="Choose the right person…"
              />
            </div>
            <div className="grid gap-3 sm:grid-cols-2">
              <TextInput
                label="From week (optional)"
                type="date"
                value={fromDate}
                onChange={(e) => {
                  setFromDate(e.target.value);
                  setPlan(null);
                }}
                hint="Leave empty to move ALL settled weeks."
              />
              <TextInput
                label="Up to week (optional, not included)"
                type="date"
                value={toDate}
                onChange={(e) => {
                  setToDate(e.target.value);
                  setPlan(null);
                }}
              />
            </div>
            <Checkbox
              label="Archive the wrong person afterwards"
              hint="Only if nothing else belongs to them. Reversible."
              checked={archiveFrom}
              onChange={(v) => {
                setArchiveFrom(v);
                setPlan(null);
              }}
            />
          </div>
        )}

        <div>
          <Button
            type="button"
            variant="secondary"
            onClick={() => void runPreview()}
            disabled={!params || previewing || done !== null}
          >
            {previewing ? <Spinner /> : 'Check what would happen'}
          </Button>
        </div>

        {previewErr && <ErrorBanner>{previewErr}</ErrorBanner>}

        {/* Step 2 — the plan, in plain language */}
        {plan && (
          <PlanView plan={plan} op={op} partyName={partyName} accountName={accountName} />
        )}

        {/* Step 3 — confirm */}
        {plan && plan.blockers.length === 0 && done === null && (
          <div className="space-y-3 rounded-[22px] border border-border/80 bg-surface-raised/60 p-4">
            <div className="text-small font-semibold text-ink">
              To confirm, type <span className="font-mono">{doomedName}</span> below.
            </div>
            <TextInput
              value={typed}
              onChange={(e) => setTyped(e.target.value)}
              placeholder={doomedName ?? ''}
              aria-label="Type the name to confirm"
            />
            {applyErr && <ErrorBanner>{applyErr}</ErrorBanner>}
            <div className="flex items-center gap-3">
              <Button
                type="button"
                variant="danger"
                onClick={() => void runApply()}
                disabled={!canConfirm}
              >
                {applying ? <Spinner /> : 'Apply this fix'}
              </Button>
              <span className="text-caption text-ink-muted">
                You'll be asked for your PIN. A safety copy is saved first.
              </span>
            </div>
          </div>
        )}

        {done && (
          <div className="flex items-start gap-3 rounded-[22px] border border-emerald-200 bg-emerald-50/90 p-4">
            <CheckCircle2 className="mt-0.5 h-5 w-5 shrink-0 text-emerald-600" strokeWidth={2} />
            <div className="min-w-0">
              <div className="text-small font-semibold text-emerald-800">Fix applied.</div>
              <div className="mt-1 text-[13px] leading-5 text-emerald-800/80">
                A safety copy of the books was saved first. You can undo this fix from the history
                below as long as no other changes are made.
              </div>
              <div className="mt-3">
                <Button type="button" variant="secondary" size="sm" onClick={onClose}>
                  Done
                </Button>
              </div>
            </div>
          </div>
        )}
      </div>
    </SectionCard>
  );
}

// ---------------------------------------------------------------------------
// Plan rendering
// ---------------------------------------------------------------------------

function PlanView({
  plan,
  op,
  partyName,
  accountName,
}: {
  plan: CorrectionPlan;
  op: CorrectionOpType;
  partyName: (id: number) => string;
  accountName: (id: number) => string;
}) {
  if (plan.blockers.length > 0) {
    return (
      <div className="space-y-3">
        <div className="text-small font-semibold text-ink">
          This fix can't run yet — here's what's in the way:
        </div>
        {plan.blockers.map((b, i) => (
          <div
            key={`${b.code}-${i}`}
            className="rounded-[20px] border border-amber-200 bg-amber-50/90 p-4"
          >
            <div className="flex items-start gap-2.5">
              <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-amber-600" strokeWidth={2} />
              <div className="min-w-0">
                <div className="text-[13px] leading-5 text-amber-900">{b.message}</div>
                {b.remedy && (
                  <div className="mt-1.5 text-[13px] leading-5 font-medium text-amber-800">
                    What to do: {b.remedy}
                  </div>
                )}
              </div>
            </div>
          </div>
        ))}
      </div>
    );
  }

  const moving = plan.changeSet.filter((c) => c.rowIds.length > 0);
  const deltas = Object.entries(plan.balanceDeltas);

  return (
    <div className="space-y-3 rounded-[22px] border border-border/80 p-4">
      <div className="text-small font-semibold text-ink">What will happen</div>

      {moving.length > 0 ? (
        <ul className="space-y-1 text-[13px] leading-6 text-ink">
          {moving.map((c) => (
            <li key={`${c.table}.${c.column}`}>
              • {rowsLabel(c.table, c.rowIds.length)}{' '}
              {op === 'merge-account'
                ? `move to ${accountName(c.toValue)}`
                : `move ${partyName(c.fromValue)} → ${partyName(c.toValue)}`}
            </li>
          ))}
        </ul>
      ) : (
        <div className="text-[13px] leading-5 text-ink-muted">No records need to move.</div>
      )}

      {plan.entityActions.length > 0 && (
        <ul className="space-y-1 text-[13px] leading-6 text-ink">
          {plan.entityActions.map((a, i) => (
            <li key={i}>
              {a.kind === 'delete-account' &&
                `• The duplicate account ${accountName(a.accountId!)} will be removed once empty.`}
              {a.kind === 'attach-alias' &&
                `• The name "${a.rawName}" will be kept as a nickname of ${accountName(
                  a.accountId!,
                )} so future sheets still match.`}
              {a.kind === 'skip-alias' &&
                `• The name "${a.rawName}" already exists as a nickname — nothing extra needed.`}
              {a.kind === 'archive-party' &&
                `• ${partyName(a.partyId!)} will be archived (hidden from lists — reversible).`}
            </li>
          ))}
        </ul>
      )}

      {deltas.length > 0 && (
        <div className="space-y-1.5">
          <div className="text-caption font-semibold uppercase tracking-[0.1em] text-ink-muted">
            Balances
          </div>
          {deltas.map(([pid, d]) => (
            <div key={pid} className="flex flex-wrap items-center gap-2 text-[13px] text-ink">
              <span className="font-medium">{partyName(Number(pid))}:</span>
              <Money amount={d.before} />
              <span className="text-ink-muted">→</span>
              <Money amount={d.after} />
            </div>
          ))}
        </div>
      )}

      {plan.globalNetUnchanged && (
        <div className="flex items-center gap-2 text-[13px] font-medium text-emerald-700">
          <CheckCircle2 className="h-4 w-4" strokeWidth={2} />
          Grand total across all books: unchanged
        </div>
      )}

      {plan.notes.length > 0 && (
        <div className="space-y-1 rounded-[16px] bg-surface-raised/70 p-3 text-[12px] leading-5 text-ink-muted">
          {plan.notes.map((n, i) => (
            <div key={i}>• {n}</div>
          ))}
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// History
// ---------------------------------------------------------------------------

function describeCorrection(
  row: CorrectionHistoryRow,
  partyName: (id: number) => string,
  accountName: (id: number) => string,
): string {
  const p = row.params as Record<string, number | string | boolean>;
  switch (row.opType) {
    case 'merge-account': {
      // The folded-away account row is deleted — its name is journalled.
      const loser = row.loserAccountName ?? accountName(Number(p.loserAccountId));
      return `Merged account ${loser} into ${accountName(Number(p.survivorAccountId))}`;
    }
    case 'merge-party':
      return `Merged ${partyName(Number(p.loserPartyId))} into ${partyName(
        Number(p.survivorPartyId),
      )}`;
    case 'move-holder-history': {
      const range =
        p.fromDate || p.toDate
          ? ` (weeks ${String(p.fromDate ?? 'start')} → ${String(p.toDate ?? 'now')})`
          : '';
      return `Moved ${accountName(Number(p.accountId))} history from ${partyName(
        Number(p.fromPartyId),
      )} to ${partyName(Number(p.toPartyId))}${range}`;
    }
  }
}

function HistoryCard({
  partyName,
  accountName,
}: {
  partyName: (id: number) => string;
  accountName: (id: number) => string;
}) {
  const qc = useQueryClient();
  const { withPin } = usePinGate();
  const [busyId, setBusyId] = useState<number | null>(null);
  const [errById, setErrById] = useState<Record<number, string>>({});

  const historyQ = useQuery<{ corrections: CorrectionHistoryRow[] }>({
    queryKey: ['corrections'],
    queryFn: () => corrections.list(),
  });

  const runUndo = async (id: number): Promise<void> => {
    setBusyId(id);
    setErrById((m) => ({ ...m, [id]: '' }));
    try {
      await withPin(async () => {
        try {
          await corrections.undo(id);
          void qc.invalidateQueries({ queryKey: ['corrections'] });
          void qc.invalidateQueries({ queryKey: ['parties'] });
          void qc.invalidateQueries({ queryKey: ['accounts'] });
        } catch (err) {
          if (err instanceof ApiError && err.status === 423) throw err;
          setErrById((m) => ({
            ...m,
            [id]: err instanceof Error ? err.message : 'The undo failed.',
          }));
        }
      });
    } finally {
      setBusyId(null);
    }
  };

  const rows = historyQ.data?.corrections ?? [];

  return (
    <SectionCard section="settings" title="Fixes applied so far">
      {historyQ.isLoading ? (
        <div className="flex items-center gap-2 text-caption text-ink-muted">
          <Spinner /> loading…
        </div>
      ) : rows.length === 0 ? (
        <EmptyState
          section="settings"
          icon={History}
          title="No fixes yet"
          description="When you apply a fix it appears here, with an Undo while it is still safe."
        />
      ) : (
        <div className="space-y-3">
          {rows.map((row) => (
            <div
              key={row.id}
              className="rounded-[20px] border border-border/70 bg-surface px-4 py-3.5 shadow-sm"
            >
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div className="min-w-0 flex-1">
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="text-caption text-ink-muted">
                      {new Date(row.appliedAt).toLocaleDateString(undefined, {
                        day: 'numeric',
                        month: 'short',
                        year: 'numeric',
                      })}
                    </span>
                    {row.status === 'rolled_back' ? (
                      <Pill tone="neutral">Undone</Pill>
                    ) : row.cleanlyUndoable ? (
                      <Pill tone="green">Undo available</Pill>
                    ) : (
                      <Pill tone="amber">Locked in</Pill>
                    )}
                  </div>
                  <div className="mt-1.5 text-small font-medium text-ink">
                    {describeCorrection(row, partyName, accountName)}
                  </div>
                  <div className="mt-1 text-[12px] leading-5 text-ink-muted">
                    {row.rowsTouched > 0 ? `${row.rowsTouched} records touched · ` : ''}
                    by {row.actor}
                    {row.status === 'rolled_back' && row.rolledBackAt
                      ? ` · undone ${new Date(row.rolledBackAt).toLocaleDateString()}`
                      : ''}
                  </div>
                  <div className="mt-1 truncate font-mono text-[11px] text-ink-muted/80">
                    Safety copy: {row.snapshotPath}
                  </div>
                  {errById[row.id] && (
                    <div className="mt-2">
                      <ErrorBanner>{errById[row.id]}</ErrorBanner>
                    </div>
                  )}
                </div>
                <div className="shrink-0">
                  {row.status === 'applied' &&
                    (row.cleanlyUndoable ? (
                      <Button
                        type="button"
                        variant="secondary"
                        size="sm"
                        onClick={() => void runUndo(row.id)}
                        disabled={busyId === row.id}
                      >
                        {busyId === row.id ? (
                          <Spinner />
                        ) : (
                          <>
                            <Undo2 className="h-4 w-4" strokeWidth={2} /> Undo
                          </>
                        )}
                      </Button>
                    ) : (
                      <span className="inline-block max-w-[180px] text-right text-caption text-ink-muted">
                        Can't undo — {row.laterChanges === 1 ? '1 later change' : `${row.laterChanges} later changes`}
                      </span>
                    ))}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </SectionCard>
  );
}
